/*
 * EwOffers (TC-08): applies and removes Everwow discount codes on the cart, never
 * swapping down, and keeps the signup a visitor earned. Site-wide: the cart (TC-09..TC-13),
 * the pop-up (TN-16..TN-18) and the footer offer use it. It computes no discount — Shopify's
 * allocations are the money (01-discount-architecture §4); it only decides which code goes on
 * the cart.
 *
 *   EwOffers.config                  #ew-offers-config (snippets/offers-config.liquid), parsed
 *   EwOffers.rankOf(code) → 0..30    0 = foreign (not in the hand-kept rank table) or a credit
 *   EwOffers.state([cart])           → Promise<{codes:[{code, applicable, rank, gated, offer,
 *                                       percent}], best, foreignLive, cart}>
 *   EwOffers.canOffer([cart])        → Promise<bool>: false with any code of rank ≥ 5 on the
 *                                       cart (applicable or not) or a live foreign code
 *   EwOffers.apply(code, {source})   → Promise<{result, cart}>, result ∈ applied |
 *       pending_checkout | pending_gift | kept_better | blocked_foreign | not_applicable | network
 *   EwOffers.remove(code)            → Promise<{result: removed|network, cart, signupStatus}>
 *   EwOffers.claim(email, {place, hp}) → Promise<{status, code, offer, percent}> (Simple mode)
 *   EwOffers.signup.get() / .set({email, code, status[, at]})
 *   EwOffers.setWriter({run, after}) the cart page's write queue and refresh
 *   document 'ew:offers-changed' {detail: {cart, result, code, source}}
 *
 * The rules (01 §4, canonical §3.2, D-37):
 * - One Everwow code at a time; a higher rank replaces a lower one; a code of equal or
 *   higher rank — applicable or not — is never replaced, nor is an applicable foreign code
 *   (Gorgias, Loox, influencer…). A dead foreign code stays on the cart next to ours.
 *   Exception: a past buyer's WELCOMEBACK7 (`source: 'claim_bought'`) replaces the signup code
 *   (rank 10, `wc10`), which a past buyer can't use; never BLACKFRIDAY10, which they can.
 * - 0 experience-gift units ⇒ nothing is written (`pending_gift`): the code waits in the
 *   stored signup and the cart applies it on its next load (TC-13), so nothing relies on how
 *   Shopify treats a code on an empty cart.
 * - A segment-gated code (THANKYOU15/30) that reads not applicable in an anonymous cart is
 *   kept, not an error: checkout applies it once the email is known (`pending_checkout`).
 *   Any other code that does not apply is taken off again and the previous codes and
 *   `_ew_offer_src` are put back (`not_applicable`).
 * - A credit (rank 0, `credit`) blocks ours while it applies, like a foreign code. A gated
 *   credit (XMAS1257/1586, Shopify customer segments) may read not applicable until checkout
 *   knows the customer, so it blocks ours from the moment it is on the cart: otherwise a stored
 *   signup code would join it in the cart and both would come off at checkout.
 * - Every request times out after 8 s and is retried once on a network error only.
 *
 * Claim mode (the claim endpoint, Turnstile) is TC-14 and not built: claim() answers
 * `paused` unless `welcome_mode` resolves to `simple`. It pushes no tracking events — its
 * callers do, since only they know the place. There is no `window.EwTrack` fallback here.
 *
 * `tidy()` (plan FIX-17) is not built: it waits on spike T-YIELD.
 */
(function () {
  const TIMEOUT_MS = 8000;
  const MAX_CODES = 5;
  const EXPERIENCE_TYPE = 'experience-gift';
  const SIGNUP_KEY = 'ew:signup';
  const SIGNUP_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  const SIGNUP_STATUSES = [
    'new',
    'existing',
    'bought',
    'subscribed',
    'removed',
  ];
  const SID_KEY = 'ew:sid';
  const SOURCES = {
    cart: 'Everwow cart email',
    popup: 'Everwow 10% pop-up',
    footer: 'Everwow footer',
  };

  let parsedConfig = null;
  let writer = null;

  /* ------------------------------------------------------------ config and ranks */

  function readConfig() {
    if (parsedConfig) return parsedConfig;
    let raw = {};
    try {
      const el = document.getElementById('ew-offers-config');
      if (el) raw = JSON.parse(el.textContent || '{}') || {};
    } catch (error) {
      raw = {};
    }
    parsedConfig = {
      offersEnabled: raw.offersEnabled === true,
      qa: raw.qa === true,
      modes: raw.modes || {},
      features: raw.features || {},
      codes: raw.codes || {},
      claimUrl: raw.claimUrl || '',
      ranks: Array.isArray(raw.ranks) ? raw.ranks : [],
      consentVersion: raw.consentVersion || '',
      turnstileSiteKey: raw.turnstileSiteKey || '',
    };
    return parsedConfig;
  }

  function upper(code) {
    return String(code == null ? '' : code)
      .trim()
      .toUpperCase();
  }

  // The rank-table entry for a code: exact entries match the whole code, prefix entries
  // its start (a rotated GIVERGIFT5B ranks 5, every EW10- pool code 10). Null = foreign.
  function entryFor(code, ranks) {
    const wanted = upper(code);
    if (!wanted) return null;
    for (let i = 0; i < ranks.length; i++) {
      const entry = ranks[i];
      const key = upper(entry.code);
      if (!key) continue;
      const hit =
        entry.match === 'exact' ? wanted === key : wanted.indexOf(key) === 0;
      if (hit) return entry;
    }
    return null;
  }

  function rankOf(code, ranks) {
    const entry = entryFor(code, ranks || readConfig().ranks);
    return entry ? entry.rank : 0;
  }

  function expGiftUnits(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    return cart.items.reduce(function (sum, item) {
      const type = String(item.product_type || '').toLowerCase();
      return type === EXPERIENCE_TYPE ? sum + (item.quantity || 0) : sum;
    }, 0);
  }

  // The cart's codes, each with what the rank table says about it.
  function describe(codes, ranks) {
    return (codes || []).map(function (entry) {
      const found = entryFor(entry.code, ranks);
      return {
        code: entry.code,
        applicable: entry.applicable !== false,
        rank: found ? found.rank : 0,
        gated: !!(found && found.gated),
        offer: found ? found.offer : '',
        percent: found ? found.percent : 0,
      };
    });
  }

  function sameCode(a, b) {
    return upper(a) === upper(b);
  }

  // A live foreign code or credit, or a gated credit, which only checkout can evaluate.
  function blocksOurs(entry) {
    return (
      entry.rank === 0 &&
      (entry.applicable || (entry.gated && entry.offer === 'credit'))
    );
  }

  /*
   * The never-swap-down decision, pure. `present` is /cart.js `discount_codes`.
   * → {result: 'write', next, previous} or {result: applied | pending_checkout |
   *   not_applicable | kept_better | blocked_foreign} with nothing to write.
   */
  function decide(present, code, source, ranks) {
    const list = describe(present, ranks);
    const wanted = entryFor(code, ranks);
    const wantRank = wanted ? wanted.rank : 0;
    const previous = list.map(function (entry) {
      return entry.code;
    });

    const same = list.find(function (entry) {
      return sameCode(entry.code, code);
    });
    if (same) {
      if (same.applicable) return { result: 'applied' };
      return { result: same.gated ? 'pending_checkout' : 'not_applicable' };
    }

    if (list.some(blocksOurs)) return { result: 'blocked_foreign' };

    const better = list.filter(function (entry) {
      return entry.rank > 0 && entry.rank >= wantRank;
    });
    if (better.length) {
      const pastBuyerSwap =
        source === 'claim_bought' &&
        wantRank === 7 &&
        better.every(function (entry) {
          return entry.rank === 10 && entry.offer === 'wc10';
        });
      if (!pastBuyerSwap) return { result: 'kept_better' };
    }

    const next = list
      .filter(function (entry) {
        return entry.rank === 0;
      })
      .map(function (entry) {
        return entry.code;
      })
      .slice(0, MAX_CODES - 1);
    next.push(String(code).trim());
    return { result: 'write', next: next, previous: previous };
  }

  /* --------------------------------------------------------------------- network */

  function isNetworkError(error) {
    return (
      !!error && (error.name === 'AbortError' || error.name === 'TypeError')
    );
  }

  // One request with an 8 s timeout; a network error (never an HTTP one) gets one retry.
  function request(url, init) {
    const once = function () {
      const controller =
        typeof AbortController === 'function' ? new AbortController() : null;
      const timer = controller
        ? setTimeout(function () {
            controller.abort();
          }, TIMEOUT_MS)
        : null;
      const options = Object.assign(
        { credentials: 'same-origin', cache: 'no-store' },
        init || {},
      );
      if (controller) options.signal = controller.signal;
      return fetch(url, options).then(
        function (response) {
          if (timer) clearTimeout(timer);
          if (!response.ok) {
            const error = new Error('HTTP ' + response.status);
            error.name = 'HttpError';
            throw error;
          }
          return response.json();
        },
        function (error) {
          if (timer) clearTimeout(timer);
          throw error;
        },
      );
    };
    return once().catch(function (error) {
      if (!isNetworkError(error)) throw error;
      return once();
    });
  }

  function readCart() {
    return request('/cart.js', { method: 'GET' });
  }

  function updateCart(payload) {
    return request('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });
  }

  // Runs one cart write: on the cart page inside its write queue, followed by its refresh
  // (the section re-renders from the cart as it now is); elsewhere straight away.
  function runWrite(work) {
    const task = function () {
      return work().then(function (outcome) {
        if (!outcome.wrote || !writer || !writer.after) return outcome;
        return Promise.resolve(writer.after()).then(
          function () {
            return outcome;
          },
          function () {
            return outcome;
          },
        );
      });
    };
    if (writer && writer.run) {
      // The queue hands the previous write's result to the task; it must not see it.
      return Promise.resolve(
        writer.run(function () {
          return task();
        }),
      );
    }
    return task();
  }

  function announce(detail) {
    try {
      document.dispatchEvent(
        new CustomEvent('ew:offers-changed', { detail: detail }),
      );
    } catch (error) {
      // No CustomEvent (tests): nothing listens there.
    }
  }

  /* ------------------------------------------------------------------ apply/remove */

  function apply(code, options) {
    const source = (options && options.source) || '';
    const ranks = readConfig().ranks;
    return runWrite(function () {
      return readCart().then(function (cart) {
        if (expGiftUnits(cart) === 0) {
          return { result: 'pending_gift', cart: cart, wrote: false };
        }
        const decision = decide(cart.discount_codes, code, source, ranks);
        if (decision.result !== 'write') {
          return { result: decision.result, cart: cart, wrote: false };
        }
        const previousSource =
          (cart.attributes && cart.attributes._ew_offer_src) || '';
        return updateCart({
          discount: decision.next.join(','),
          attributes: { _ew_offer_src: source },
        }).then(function (after) {
          const landed = (after.discount_codes || []).find(function (entry) {
            return sameCode(entry.code, code);
          });
          const wanted = entryFor(code, ranks);
          if (landed && landed.applicable !== false) {
            return { result: 'applied', cart: after, wrote: true };
          }
          if (landed && wanted && wanted.gated) {
            return { result: 'pending_checkout', cart: after, wrote: true };
          }
          return updateCart({
            discount: decision.previous.join(','),
            attributes: { _ew_offer_src: previousSource },
          }).then(function (restored) {
            return { result: 'not_applicable', cart: restored, wrote: true };
          });
        });
      });
    })
      .catch(function () {
        return { result: 'network', cart: null, wrote: false };
      })
      .then(function (outcome) {
        const answer = { result: outcome.result, cart: outcome.cart };
        announce({
          cart: outcome.cart,
          result: outcome.result,
          code: code,
          source: source,
        });
        return answer;
      });
  }

  // Takes one code off the cart and nothing else. The attribution goes with the last
  // Everwow code. A stored signup for that code is marked `removed`, so the cart does not
  // put it back on the next load (PO-TC10); its previous status comes back for an Undo.
  function remove(code) {
    const ranks = readConfig().ranks;
    return runWrite(function () {
      return readCart().then(function (cart) {
        const present = cart.discount_codes || [];
        const next = present
          .filter(function (entry) {
            return !sameCode(entry.code, code);
          })
          .map(function (entry) {
            return entry.code;
          });
        if (next.length === present.length) {
          return { result: 'removed', cart: cart, wrote: false };
        }
        const payload = { discount: next.join(',') };
        const ourLeft = next.some(function (entry) {
          return !!entryFor(entry, ranks);
        });
        if (!ourLeft) payload.attributes = { _ew_offer_src: '' };
        return updateCart(payload).then(function (after) {
          return { result: 'removed', cart: after, wrote: true };
        });
      });
    })
      .catch(function () {
        return { result: 'network', cart: null, wrote: false };
      })
      .then(function (outcome) {
        let signupStatus = null;
        if (outcome.result === 'removed') {
          const stored = signup.get();
          if (
            stored &&
            sameCode(stored.code, code) &&
            stored.status !== 'removed'
          ) {
            signupStatus = stored.status;
            signup.set(Object.assign({}, stored, { status: 'removed' }));
          }
        }
        announce({
          cart: outcome.cart,
          result: outcome.result,
          code: code,
          source: 'remove',
        });
        return {
          result: outcome.result,
          cart: outcome.cart,
          signupStatus: signupStatus,
        };
      });
  }

  function state(cart) {
    const ranks = readConfig().ranks;
    const read = cart ? Promise.resolve(cart) : readCart();
    return read.then(function (current) {
      const codes = describe(current.discount_codes, ranks);
      let best = null;
      codes.forEach(function (entry) {
        if (entry.rank > 0 && (!best || entry.rank > best.rank)) best = entry;
      });
      return {
        codes: codes,
        best: best,
        foreignLive: codes.some(blocksOurs),
        cart: current,
      };
    });
  }

  function canOffer(cart) {
    return state(cart).then(function (current) {
      return !current.foreignLive && !(current.best && current.best.rank >= 5);
    });
  }

  /* ------------------------------------------------------------------------ signup */

  function storage(kind) {
    try {
      return window[kind] || null;
    } catch (error) {
      return null;
    }
  }

  function validSignup(value, now) {
    if (!value || typeof value !== 'object') return null;
    if (SIGNUP_STATUSES.indexOf(value.status) === -1) return null;
    if (typeof value.at !== 'number' || now - value.at > SIGNUP_TTL_MS)
      return null;
    return value;
  }

  const signup = {
    get: function () {
      const store = storage('localStorage');
      if (!store) return null;
      try {
        return validSignup(JSON.parse(store.getItem(SIGNUP_KEY)), Date.now());
      } catch (error) {
        return null;
      }
    },
    set: function (value) {
      const store = storage('localStorage');
      if (!store || !value) return false;
      const entry = {
        email: String(value.email || ''),
        code: String(value.code || ''),
        status: value.status,
        at: typeof value.at === 'number' ? value.at : Date.now(),
      };
      if (SIGNUP_STATUSES.indexOf(entry.status) === -1) return false;
      try {
        store.setItem(SIGNUP_KEY, JSON.stringify(entry));
        return true;
      } catch (error) {
        return false;
      }
    },
  };

  function sessionId() {
    const store = storage('sessionStorage');
    try {
      let sid = store && store.getItem(SID_KEY);
      if (!sid) {
        sid =
          window.crypto && window.crypto.randomUUID
            ? window.crypto.randomUUID()
            : Date.now().toString(36) + Math.random().toString(36).slice(2);
        if (store) store.setItem(SID_KEY, sid);
      }
      return sid;
    } catch (error) {
      return '';
    }
  }

  /* ------------------------------------------------------------------------- claim */

  // Simple mode: a Klaviyo subscribe with the place on the profile (RA-19's flow filters
  // on `ew_signup_place`), then the shared welcome code, written to the stored signup
  // before any caller applies it. The caller applies it, with its own source.
  function claim(email, options) {
    const opts = options || {};
    const place = SOURCES[opts.place] ? opts.place : 'cart';
    const config = readConfig();
    const none = { code: null, offer: null, percent: null };
    const answer = function (status, extra) {
      return Object.assign({ status: status }, none, extra || {});
    };
    sessionId();
    if (config.modes.welcome !== 'simple') {
      return Promise.resolve(answer('paused'));
    }
    const address = String(email || '')
      .replace(/\s+/g, '')
      .toLowerCase();
    // A filled honeypot is a bot: it gets the same answer and nothing is sent.
    if (opts.hp) return Promise.resolve(answer('subscribed'));
    if (!window.EwKlaviyo) return Promise.resolve(answer('network'));
    return window.EwKlaviyo.subscribe({
      email: address,
      source: SOURCES[place],
      properties: { ew_signup_place: place },
    }).then(function (result) {
      if (!result || !result.ok) return answer('network');
      const code = config.codes.welcome || '';
      const entry = entryFor(code, config.ranks);
      if (code)
        signup.set({ email: address, code: code, status: 'subscribed' });
      return answer('subscribed', {
        code: code || null,
        offer: entry ? entry.offer : null,
        percent: entry ? entry.percent : null,
      });
    });
  }

  function setWriter(value) {
    writer = value || null;
  }

  const api = {
    rankOf: function (code) {
      return rankOf(code);
    },
    entryFor: function (code) {
      return entryFor(code, readConfig().ranks);
    },
    state: state,
    canOffer: canOffer,
    apply: apply,
    remove: remove,
    claim: claim,
    signup: signup,
    setWriter: setWriter,
    _test: {
      entryFor: entryFor,
      rankOf: rankOf,
      expGiftUnits: expGiftUnits,
      decide: decide,
      validSignup: validSignup,
      reset: function () {
        parsedConfig = null;
        writer = null;
      },
    },
  };
  Object.defineProperty(api, 'config', {
    enumerable: true,
    get: readConfig,
  });
  window.EwOffers = api;
})();
